import { useState } from "react";
import { api, formatApiError, WEEKDAYS_IT, fmtItalianDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarClock, X } from "lucide-react";

function emptyDraft(studio) {
  return {
    effective_date: "",
    name: studio.name,
    address: studio.address,
    maps_url: studio.maps_url,
    schedule: studio.schedule.map((s) => ({ ...s })),
  };
}

export default function ScheduledChangeEditor({ studio, onSaved }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => emptyDraft(studio));
  const [saving, setSaving] = useState(false);
  const pending = studio.scheduled_change;

  const save = async () => {
    if (!draft.effective_date) { toast.error("Indica la data da cui parte il cambiamento"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/studios/${studio.id}/scheduled-change`, draft);
      toast.success("Cambiamento programmato salvato");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const cancelPending = async () => {
    if (!window.confirm("Annullare il cambiamento programmato?")) return;
    try {
      await api.delete(`/admin/studios/${studio.id}/scheduled-change`);
      toast.success("Cambiamento programmato annullato");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (pending) {
    return (
      <div data-testid={`studio-${studio.id}-pending-change`} className="mt-5 rounded-xl border border-[#D4A373] bg-[#FEF6EC] p-4">
        <div className="flex items-start gap-3">
          <CalendarClock size={18} className="text-[#B8730E] mt-0.5 shrink-0"/>
          <div className="flex-1 text-sm">
            <div className="font-medium text-[#1A231F]">Cambiamento programmato dal {fmtItalianDate(pending.effective_date, { withYear: true })}</div>
            <div className="text-[#5C7066] mt-1">{pending.name} · {pending.address}</div>
            <div className="text-[#5C7066] mt-1">{pending.schedule.map((s, i) => `${WEEKDAYS_IT[s.weekday]} ${s.start}-${s.last_start}`).join(" · ")}</div>
          </div>
          <button data-testid={`studio-${studio.id}-cancel-pending`} onClick={cancelPending} title="Annulla" className="text-[#B85C5C] hover:bg-[#B85C5C]/10 p-1.5 rounded-lg shrink-0"><X size={14}/></button>
        </div>
      </div>
    );
  }

  if (!open) {
    return (
      <button data-testid={`studio-${studio.id}-schedule-change-btn`} onClick={() => { setDraft(emptyDraft(studio)); setOpen(true); }} className="mt-5 text-sm text-[#2D4A3E] hover:underline flex items-center gap-1.5">
        <CalendarClock size={14}/> Programma un cambiamento futuro
      </button>
    );
  }

  return (
    <div className="mt-5 rounded-xl border border-[#E0E5E2] bg-[#FAF9F6] p-4">
      <div className="overline mb-3">Nuovo cambiamento programmato</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>In vigore dal</Label><Input data-testid={`studio-${studio.id}-pending-date`} type="date" value={draft.effective_date} onChange={e=>setDraft({...draft, effective_date: e.target.value})} className="h-11 mt-1 rounded-xl"/></div>
        <div><Label>Nuovo nome</Label><Input data-testid={`studio-${studio.id}-pending-name`} value={draft.name} onChange={e=>setDraft({...draft, name: e.target.value})} className="h-11 mt-1 rounded-xl"/></div>
        <div><Label>Nuovo indirizzo</Label><Input data-testid={`studio-${studio.id}-pending-address`} value={draft.address} onChange={e=>setDraft({...draft, address: e.target.value})} className="h-11 mt-1 rounded-xl"/></div>
        <div><Label>Nuovo link Google Maps</Label><Input data-testid={`studio-${studio.id}-pending-maps`} value={draft.maps_url} onChange={e=>setDraft({...draft, maps_url: e.target.value})} className="h-11 mt-1 rounded-xl"/></div>
      </div>
      <div className="mt-4">
        <div className="overline mb-2">Nuovi orari di ricevimento</div>
        <div className="space-y-2">
          {draft.schedule.map((sc, idx) => (
            <div key={idx} className="grid sm:grid-cols-4 gap-2 items-center">
              <select value={sc.weekday} onChange={e=>setDraft({...draft, schedule: draft.schedule.map((x,i)=>i===idx?{...x, weekday: parseInt(e.target.value)}:x)})} className="h-11 rounded-xl border border-[#E0E5E2] bg-white px-3">
                {WEEKDAYS_IT.map((w,wi)=>(<option key={wi} value={wi}>{w}</option>))}
              </select>
              <Input type="time" value={sc.start} onChange={e=>setDraft({...draft, schedule: draft.schedule.map((x,i)=>i===idx?{...x, start: e.target.value}:x)})} className="h-11 rounded-xl"/>
              <Input type="time" value={sc.last_start} onChange={e=>setDraft({...draft, schedule: draft.schedule.map((x,i)=>i===idx?{...x, last_start: e.target.value}:x)})} className="h-11 rounded-xl"/>
              <button onClick={()=>setDraft({...draft, schedule: draft.schedule.filter((_,i)=>i!==idx)})} className="text-[#B85C5C] hover:underline text-sm text-left">Rimuovi</button>
            </div>
          ))}
        </div>
        <button onClick={()=>setDraft({...draft, schedule: [...draft.schedule, {weekday: 0, start: "09:00", last_start: "12:00"}]})} className="mt-3 text-sm text-[#2D4A3E] hover:underline">+ Aggiungi fascia</button>
      </div>
      <div className="mt-4 flex gap-2 justify-end">
        <Button variant="outline" onClick={()=>setOpen(false)} className="h-11 rounded-xl">Annulla</Button>
        <Button data-testid={`studio-${studio.id}-pending-save`} onClick={save} disabled={saving} className="h-11 rounded-xl bg-[#2D4A3E] hover:bg-[#1A2F26]">Salva cambiamento programmato</Button>
      </div>
    </div>
  );
}
